import cartUtils from '../../utils/cart';
import { v4 as uuidv4 } from 'uuid';

function applyPersonalizationToCartItems(cartItems, payload) {
  const {
    cartObject,
    personalizationSetToSameValue, // indicates that personalization on all personalized products in the cart should be set to the same value
    personalizationProduct,
  } = payload;

  let setPersonalizationOnAllPersonalizableProductsInCart = false;

  // TODO: update this logic to use a flag set on the studio?
  // We assume that when the personalizationProduct is present that:
  // 1. we establish personalization on all personalizable products in the cart
  if (personalizationProduct) {
    setPersonalizationOnAllPersonalizableProductsInCart = true;
  }

  const cartObjectPersonalizationValue = cartObject.personalizationValue;

  // get child item personalization values, filter out null, undefined, and empty strings
  const cartObjectChildPersonalizationValues = (cartObject.children || [])
    .map((child) => child.personalizationValue)
    .filter((value) => value !== undefined && value !== null && value !== '');

  let reviewWhetherCartItemsNeedPersonalizationUpdate = false;
  let submittedPersonalizationValue;

  // If the item being added or the child items have personalization values,
  // get the value and set the flag to review whether the cart items need to be reviewed/updated
  if (
    (cartObjectPersonalizationValue && cartObjectPersonalizationValue !== '') ||
    cartObjectChildPersonalizationValues.length > 0
  ) {
    reviewWhetherCartItemsNeedPersonalizationUpdate = true;
    submittedPersonalizationValue =
      cartObjectPersonalizationValue || cartObjectChildPersonalizationValues[0];
  }

  if (
    !reviewWhetherCartItemsNeedPersonalizationUpdate ||
    !personalizationSetToSameValue
  ) {
    return cartItems;
  }

  // In this case, ensure the same value is set on all already-personalized items in the cart

  if (!setPersonalizationOnAllPersonalizableProductsInCart) {
    return cartItems.map((item) => {
      if (
        submittedPersonalizationValue &&
        submittedPersonalizationValue !== '' &&
        item.personalizationValue &&
        item.personalizationValue !== ''
      ) {
        return {
          ...item,
          personalizationValue: submittedPersonalizationValue,
        };
      }

      // Check childProducts
      if (item.children) {
        const updatedChildProducts = item.children.map((childItem) => {
          if (
            submittedPersonalizationValue &&
            submittedPersonalizationValue !== '' &&
            childItem.personalizationValue &&
            childItem.personalizationValue !== ''
          ) {
            return {
              ...childItem,
              personalizationValue: submittedPersonalizationValue,
            };
          }
          return childItem;
        });
        return { ...item, children: updatedChildProducts };
      }

      return item;
    });
  }

  // In this case, ensure the same value is set on all already-personalized items in the cart AND
  // any non-personalized but personalizable items in the cart

  return cartItems.map((item) => {
    if (
      item.personalizationStatus === 'personalization_optional' ||
      item.personalizationStatus === 'personalization_required'
    ) {
      return {
        ...item,
        personalizationValue: submittedPersonalizationValue,
      };
    }

    // Check childProducts
    if (item.children) {
      const updatedChildProducts = item.children.map((childItem) => {
        if (
          childItem.personalizationStatus === 'personalization_optional' ||
          childItem.personalizationStatus === 'personalization_required'
        ) {
          return {
            ...childItem,
            personalizationValue: submittedPersonalizationValue,
          };
        }
        return childItem;
      });
      return { ...item, children: updatedChildProducts };
    }

    return item;
  });
}

function applyRequiredFieldsToCartItems(cartItems, payload) {
  const { cartObject, personalizationSetToSameValue } = payload;

  if (!personalizationSetToSameValue) return cartItems;

  // Collect submitted field values from the incoming cart object and its children (YPX)
  const submittedFields = [
    ...(cartObject.requiredFields || []),
    ...(cartObject.children || []).flatMap(
      (child) => child.requiredFields || [],
    ),
  ].filter((f) => f && f.value != null && f.value !== '');

  if (submittedFields.length === 0) return cartItems;

  const fieldValueByKey = {};
  submittedFields.forEach((f) => {
    if (f.key) fieldValueByKey[f.key] = f.value;
  });

  const updateFields = (fields) => {
    if (!fields || fields.length === 0) return fields;
    return fields.map((field) => {
      if (field.key && fieldValueByKey[field.key] !== undefined) {
        return { ...field, value: fieldValueByKey[field.key] };
      }
      return field;
    });
  };

  return cartItems.map((item) => {
    const children =
      item.children &&
      item.children.map((child) => ({
        ...child,
        requiredFields: updateFields(child.requiredFields),
      }));

    return {
      ...item,
      ...(children ? { children } : {}),
      requiredFields: updateFields(item.requiredFields),
    };
  });
}

// When a previously-removed optional incentive is added back to the cart,
// drop it from the opt-out ignore lists so eligibility checks and the cart's
// "Available Bonuses" section treat it as a normal in-cart incentive again
// (the lists otherwise only ever grow). Order-level declines are keyed by
// product id, cart-level ones by SKU (ENG-2043 R3) — the id is still cleared
// from the cart-level list for rows served without a sku.
export function clearReAddedOptionalIncentiveIgnores(
  state,
  { productId, sku, orderKey },
) {
  const cartLevelIgnores = state.optionalCartLevelIncentiveSkusToIgnore || [];
  const ignoresByOrder = state.optionalIncentiveProductIdsToIgnoreByOrder || {};
  const orderIgnores = ignoresByOrder[orderKey] || [];
  const updates = {};
  const cartLevelKeys = [sku, productId].filter(
    (value) => value !== null && value !== undefined,
  );

  if (cartLevelIgnores.some((entry) => cartLevelKeys.includes(entry))) {
    updates.optionalCartLevelIncentiveSkusToIgnore = cartLevelIgnores.filter(
      (entry) => !cartLevelKeys.includes(entry),
    );
  }

  if (orderIgnores.includes(productId)) {
    const remainingOrderIgnores = orderIgnores.filter((id) => id !== productId);
    const newIgnoresByOrder = { ...ignoresByOrder };

    if (remainingOrderIgnores.length > 0) {
      newIgnoresByOrder[orderKey] = remainingOrderIgnores;
    } else {
      delete newIgnoresByOrder[orderKey];
    }

    updates.optionalIncentiveProductIdsToIgnoreByOrder = newIgnoresByOrder;
  }

  return updates;
}

// Keep the params already on the order when they can actually reload the
// shoot; otherwise take the incoming ones if THEY can; otherwise keep
// whatever we have (existing first, so a later keyless add never replaces an
// earlier keyless capture with an equivalent one).
const pickShootLoadParams = (existing, incoming) => {
  if (existing?.shootKey) return existing;
  if (incoming?.shootKey) return incoming;

  return existing || incoming || null;
};

export const handleAddToCart = (state, payload) => {
  const {
    additionalData,
    cartObject,
    isPrepay,
    orderProductContext,
    orderKey: requestedOrderKey,
    selectedStudent,
    shoot,
    personalizationSetToSameValue, // indicates that personalization on all personalized products in the cart should be set to the same value
    personalizationProduct,
    cartLevelIncentiveProducts,
    orderLevelIncentiveProducts,
    optionalIncentivesEnabled,
    shootLoadParams,
  } = payload;

  const studentId = selectedStudent?.id;
  const orderKey =
    requestedOrderKey || `${shoot.event_id || shoot.id}-${studentId}`;
  const currentOrder = state.orders[orderKey];

  // An explicit key is an edit of an existing order, never permission to
  // synthesize a global-event/target-student hybrid order.
  if (requestedOrderKey && !currentOrder) return state;

  if (studentId) {
    let newCartObject;
    let newCartItemIds;
    let newPersonalizationProduct;
    let personalization = {};
    let retouching = {};
    let interstitialProductsPrompted = [];
    let preCartOfferProductsPrompted = [];
    let uuid = state.uuid;

    if (currentOrder) {
      const variantId = cartUtils.getNextVariantId(
        currentOrder.cartItems,
        cartObject.productId,
      );

      newCartObject = cartUtils.createNewCartItem(cartObject, variantId);
      newCartItemIds = [...currentOrder.cartItemIds, newCartObject.id];
      retouching = currentOrder.retouching || {};
      personalization = currentOrder.personalization || {};
      interstitialProductsPrompted =
        currentOrder.interstitialProductsPrompted || [];
      preCartOfferProductsPrompted =
        currentOrder.preCartOfferProductsPrompted || [];
    } else {
      newCartObject = cartUtils.createNewCartItem(cartObject);
      newCartItemIds = [newCartObject.id];
      retouching = {
        shouldPrompt: true,
      };
      personalization = {
        shouldPrompt: true,
        value: '',
      };
    }

    // Check for personalizationProduct and that its id is not the same as cartObject's
    if (
      !cartObject.isPersonalization &&
      personalizationProduct &&
      (personalizationProduct.id !== cartObject.productId ||
        personalizationProduct.productId !== cartObject.productId)
    ) {
      newPersonalizationProduct = cartUtils.createNewPersonalizationCartItem(
        personalizationProduct,
      );

      // // Check if personalizationProduct is not already in the cart
      // if (
      //   !state.orders[studentId]?.cartItems?.find(
      //     (item) => item.id === newPersonalizationProduct.id,
      //   )
      // ) {
      //   // Add newPersonalizationProduct to cartItems and cartItemIds
      //   newCartItemIds.push(newPersonalizationProduct.id);

      //   newCartObject = [...newCartObject, newPersonalizationProduct];
      // }
    }

    if (Object.keys(state.orders).length === 0 && uuid === null) {
      uuid = uuidv4(); // Generate UUID for the order
    }

    const existingItems = currentOrder?.cartItems || [];
    const updatedExisting = applyPersonalizationToCartItems(
      existingItems,
      payload,
    );
    const updatedWithFields = applyRequiredFieldsToCartItems(
      updatedExisting,
      payload,
    );

    // Create default order structure
    let newOrder = {
      additionalData: currentOrder?.additionalData || additionalData,
      productConfig: state.productConfig,
      cartItemIds: newCartItemIds,
      cartItems: [...updatedWithFields, newCartObject],
      retouching,
      personalization,
      interstitialProductsPrompted,
      preCartOfferProductsPrompted,
      isPrepay: currentOrder?.isPrepay ?? isPrepay,
      productContext: orderProductContext || currentOrder?.productContext,
      event: currentOrder?.event || {
        id: shoot.event_id || shoot.id,
        dp2JobId: shoot.dp2_job_id,
        name: shoot.name,
        singleChargeForBackgroundUpgrades:
          shoot.single_charge_for_background_upgrades,
        chargeForYpxBackgroundUpgrades:
          shoot.charge_for_ypx_background_upgrades,
        currentPhaseIdentifier: shoot.current_phase_identifier || null,
        currentPhaseId: shoot.current_phase_id || null,
      },
      priceList: currentOrder?.priceList || {
        id: shoot.pricing_model.id,
        name: shoot.pricing_model.name,
        shippingPrice: shoot.pricing_model.shipping_price,
      },
      student: currentOrder?.student || {
        id: selectedStudent.id,
        firstName: selectedStudent.firstName || selectedStudent.first_name,
        lastName: selectedStudent.lastName || selectedStudent.last_name,
      },
      // This shoot's cart-level incentive catalog, captured while its shoot
      // data is loaded, so multi-school carts can evaluate cart-level
      // offers across every price list in the cart (ENG-2965; consumed via
      // cartUtils.unionCartLevelIncentiveProducts). The freshest capture
      // wins — thresholds and active status change mid-season — and the
      // GET_SHOOT_DATA case in the v3Order reducer refreshes it on every
      // catalog load. Orders persisted before this field existed simply
      // contribute nothing to the union.
      incentiveProducts:
        // A foreign-event edit cannot refresh an unknown legacy catalog from
        // the active event. Keep undefined so reconciliation still knows that
        // this order cannot prove which cart-level benefits it offers.
        requestedOrderKey && cartLevelIncentiveProducts === undefined
          ? currentOrder?.incentiveProducts
          : cartLevelIncentiveProducts ?? currentOrder?.incentiveProducts ?? [],
      // This shoot's order-level incentive rows, kept apart from the
      // cart-level stash above (ENG-2043 R2) so the ENG-2965 union stays
      // cart-level-only. Refreshed on every add and on GET_SHOOT_DATA.
      orderLevelIncentiveProducts:
        orderLevelIncentiveProducts ??
        currentOrder?.orderLevelIncentiveProducts ??
        [],
      // The owning company's opt-in flag (R1). An order persisted before
      // this field existed carries `undefined`, which the opt-in rule reads
      // as "unknown", not as "opt-in".
      optionalIncentivesEnabled:
        optionalIncentivesEnabled ??
        currentOrder?.optionalIncentivesEnabled ??
        false,
      // The shoot context a later re-add needs. Normally written once, on the
      // order's first add, and NOT overwritten afterwards — the parent may be
      // adding through a different gallery of the same shoot.
      //
      // The exception is a params object with no `shootKey`:
      // buildShootLoadParamsForOrder always returns an object, so an order
      // whose first add happened without a key (nothing in additionalData,
      // no shoot.key) would otherwise be pinned to unusable params forever
      // and could never be reloaded — canReloadShootForOrder requires the
      // key. So the first usable params win, whenever they arrive.
      shootLoadParams: pickShootLoadParams(
        currentOrder?.shootLoadParams,
        shootLoadParams,
      ),
    };

    if (
      newPersonalizationProduct &&
      !state.orders[orderKey]?.cartItems?.find(
        (item) => item.id === newPersonalizationProduct.id,
      )
    ) {
      newOrder = {
        ...newOrder,
        cartItems: [...newOrder.cartItems, newPersonalizationProduct],
        cartItemIds: [...newOrder.cartItemIds, newPersonalizationProduct.id],
      };
    }

    return {
      ...state,
      ...clearReAddedOptionalIncentiveIgnores(state, {
        productId: cartObject.productId,
        sku: cartObject.sku ?? null,
        orderKey,
      }),
      // The re-add marker is spent the moment its bonus is back in the cart
      // (ENG-2043 R7). Nothing downstream may read it to decide routing —
      // postConfigRoutingMiddleware keys that off the action payload.
      optionalIncentiveToConfigure:
        cartUtils.optionalIncentiveMarkerMatchesProduct(
          state.optionalIncentiveToConfigure,
          {
            productId: cartObject.productId,
            sku: cartObject.sku ?? null,
            incentiveAppliesTo: cartObject.incentiveAppliesTo,
          },
        )
          ? null
          : state.optionalIncentiveToConfigure,
      productConfig: {},
      uuid,
      orders: {
        ...state.orders,
        [orderKey]: newOrder,
      },
    };
  }

  return { ...state };
};
